import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useSocket } from '../../contexts/SocketContext'
import { api } from '../../utils/api'
import toast from 'react-hot-toast'

const ChatControl = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { joinChat, categorizeComment: socketCategorizeComment, toggleCommentVisibility: socketToggleVisibility, dismissComment: socketDismissComment, onNewComments, onCommentCategorized, onCommentVisibilityUpdated, onCommentDismissed } = useSocket()

  const [chatConfig, setChatConfig] = useState(null)
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetching, setFetching] = useState(false)
  const [categorizingComments, setCategorizingComments] = useState(new Set())

  useEffect(() => {
    fetchChatConfig()
    joinChat(id)

    // Set up real-time listeners
    onNewComments((data) => {
      if (data.chatId === id) {
        setComments(prev => [...data.comments, ...prev])
        toast.success(`${data.comments.length} new comments received`)
      }
    })

    onCommentCategorized((data) => {
      console.log('Received comment-categorized event:', data)
      if (data.chatId === id) {
        console.log(`Updating comment ${data.commentId} category to ${data.category}`)
        setComments(prev => prev.map(comment =>
          comment.id === data.commentId ? { ...comment, category: data.category } : comment
        ))
        // Remove from categorizing set when response is received
        setCategorizingComments(prev => {
          const newSet = new Set(prev)
          newSet.delete(data.commentId)
          return newSet
        })
      }
    })

    onCommentVisibilityUpdated((data) => {
      if (data.chatId === id) {
        setComments(prev => prev.map(comment =>
          comment.id === data.commentId ? { ...comment, isVisible: data.isVisible } : comment
        ))
      }
    })

    onCommentDismissed((data) => {
      if (data.chatId === id) {
        setComments(prev => prev.filter(comment => comment.id !== data.commentId))
      }
    })

    return () => {
      // Cleanup listeners
    }
  }, [id, onNewComments, onCommentCategorized, onCommentVisibilityUpdated, onCommentDismissed])

  const fetchChatConfig = async () => {
    try {
      console.log(`Fetching chat config for ID: ${id}`)
      const response = await api.get(`/chat/${id}`)
      console.log('Chat config response:', response.data)
      setChatConfig(response.data.chatConfig)
      fetchComments()
    } catch (error) {
      console.error('Failed to fetch chat config:', error)
      console.error('Error details:', error.response?.data)
      toast.error('Failed to load chat configuration')
      navigate('/chat')
    } finally {
      setLoading(false)
    }
  }

  const fetchComments = async () => {
    setFetching(true)
    try {
      // First, try to start polling if not already active
      try {
        console.log(`Starting polling for chat ID: ${id}`)
        await api.post(`/chat/${id}/start-polling`)
        toast.success('Started fetching comments from YouTube')
      } catch (pollingError) {
        // If polling is already active or fails, that's okay - we'll still fetch existing comments
        if (pollingError.response?.status === 400 && pollingError.response?.data?.message?.includes('already active')) {
          console.log('Polling already active')
        } else {
          console.warn('Failed to start polling (may already be active):', pollingError.response?.data || pollingError.message)
          // Show error only if it's not about already being active
          if (!pollingError.response?.data?.message?.includes('already active')) {
            toast.error(pollingError.response?.data?.message || 'Failed to start polling. Fetching existing comments...')
          }
        }
      }

      // Wait a moment for polling to start fetching comments
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Fetch existing comments from database
      console.log(`Fetching comments for chat ID: ${id}`)
      const response = await api.get(`/chat/${id}/comments`)
      console.log('Comments response:', response.data)
      setComments(response.data.comments)
      console.log(`Loaded ${response.data.comments.length} comments`)

      if (response.data.comments.length === 0) {
        toast('No comments found. Make sure the YouTube stream has live chat enabled and is currently live.')
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error)
      console.error('Error details:', error.response?.data)
      toast.error('Failed to load comments')
    } finally {
      setFetching(false)
    }
  }

  const categorizeComment = (commentId, category) => {
    console.log(`Categorizing comment ${commentId} as ${category}`)

    // Add to categorizing set for loading state
    setCategorizingComments(prev => new Set(prev).add(commentId))

    // Update local state immediately for instant visual feedback
    setComments(prev => prev.map(comment =>
      comment.id === commentId ? { ...comment, category: category } : comment
    ))

    // Send to socket for persistence
    try {
      socketCategorizeComment(id, commentId, category)
      console.log(`Socket emit successful for comment ${commentId}`)

      // Set a timeout to remove loading state if no response comes back
      setTimeout(() => {
        setCategorizingComments(prev => {
          const newSet = new Set(prev)
          if (newSet.has(commentId)) {
            console.warn(`Timeout: Removing loading state for comment ${commentId}`)
            newSet.delete(commentId)
          }
          return newSet
        })
      }, 5000) // 5 second timeout

    } catch (error) {
      console.error('Socket emit failed:', error)
      toast.error('Failed to save categorization')
      // Remove from categorizing set on error
      setCategorizingComments(prev => {
        const newSet = new Set(prev)
        newSet.delete(commentId)
        return newSet
      })
    }

    // Show immediate feedback
    toast.success(`Comment categorized as ${category}`)
  }

  const toggleCommentVisibility = (commentId, isVisible) => {
    // Update local state immediately for instant visual feedback
    setComments(prev => prev.map(comment =>
      comment.id === commentId ? { ...comment, isVisible: isVisible } : comment
    ))
    // Send to socket for persistence
    socketToggleVisibility(id, commentId, isVisible)
  }

  const dismissComment = (commentId) => {
    // Update local state immediately for instant visual feedback
    setComments(prev => prev.filter(comment => comment.id !== commentId))
    // Send to socket for persistence
    socketDismissComment(id, commentId)
  }

  const getCategoryColor = (category) => {
    switch (category) {
      case 'IMPORTANT': return 'border-red-500'
      case 'QUESTION': return 'border-blue-500'
      case 'COMMENT': return 'border-green-500'
      default: return 'border-gray-500'
    }
  }

  const getSuperChatColor = (amountMicros) => {
    if (!amountMicros) return ''
    const amount = amountMicros / 1000000
    if (amount >= 10) return 'border-yellow-500 bg-yellow-900/20'
    if (amount >= 5) return 'border-yellow-400 bg-yellow-800/20'
    return 'border-yellow-300 bg-yellow-700/20'
  }

  if (loading) {
    return (
      <div className="bg-[#1c1c1c] text-white p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="spinner w-12 h-12 mx-auto mb-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white">Loading chat control...</p>
        </div>
      </div>
    )
  }

  if (!chatConfig) {
    return (
      <div className="bg-[#1c1c1c] text-white p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-white text-xl">Chat configuration not found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="px-6 pb-6">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/chat')}
            className="text-white hover:text-gray-300 transition-colors flex items-center gap-2 font-avenir"
          >
            <span>&lt;</span>
            BACK
          </button>
        </div>

        {/* Title and Subtitle Section */}
        <div className="mb-8">
          <div className="flex items-center gap-8 mb-6">
            <div className="flex-1">
              <div className="text-white text-2xl font-bold mb-2">
                {chatConfig.name || 'CHAT CONTROL'}
              </div>
              <div className="text-white text-lg">
                YouTube Live Chat Moderation
              </div>
            </div>
            <div className="flex items-center gap-4 bg-[#1c1c1c] p-6">
              <button
                onClick={fetchComments}
                disabled={fetching}
                className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-6 py-3 font-medium transition-colors"
              >
                {fetching ? (
                  <div className="flex items-center gap-2">
                    <div className="spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Fetching...
                  </div>
                ) : (
                  'Fetch Comments'
                )}
              </button>
              <Link
                to={`/chat/${id}/host`}
                className="bg-transparent border border-white text-white hover:bg-white hover:text-black px-6 py-3 font-medium transition-colors"
              >
                Go to Host View
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-[#1c1c1c] p-6">
          <h2 className="text-white text-xl text-center font-bold mb-6">Live Chat Comments</h2>

          {/* Comments List */}
          <div className="space-y-4">
            {comments.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg">No comments available</p>
                <p className="text-gray-500 text-sm mt-2">
                  Click "Fetch Comments" to load YouTube chat messages
                </p>
              </div>
            ) : (
              comments.map((comment) => (
                <div
                  key={comment.id}
                  className={`p-4 rounded border-l-4 ${getCategoryColor(comment.category)} ${comment.amountMicros ? getSuperChatColor(comment.amountMicros) : 'bg-gray-800'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {comment.authorThumbnailUrl && (
                          <img
                            src={comment.authorThumbnailUrl}
                            alt={comment.authorName}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => {
                              console.warn('Thumbnail failed to load for', comment.authorName);
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <div className="text-white font-medium">
                            {comment.authorName}
                            {comment.amountMicros && (
                              <span className="ml-2 text-yellow-400 font-bold">
                                ${(comment.amountMicros / 1000000).toFixed(2)}
                              </span>
                            )}
                          </div>
                          <div className="text-gray-400 text-sm">
                            {comment.category || 'UNCATEGORIZED'}
                          </div>
                        </div>
                      </div>
                      <div className="text-gray-300 mb-3">
                        {comment.commentText}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => categorizeComment(comment.id, 'IMPORTANT')}
                          disabled={categorizingComments.has(comment.id)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-all duration-200 ${comment.category === 'IMPORTANT'
                              ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg'
                              : 'bg-transparent border border-white text-white hover:bg-white hover:text-black hover:border-red-300'
                            } ${categorizingComments.has(comment.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {categorizingComments.has(comment.id) ? '...' : 'IMPORTANT'}
                        </button>
                        <button
                          onClick={() => categorizeComment(comment.id, 'QUESTION')}
                          disabled={categorizingComments.has(comment.id)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-all duration-200 ${comment.category === 'QUESTION'
                              ? 'bg-blue-600 text-white border-2 border-blue-400 shadow-lg'
                              : 'bg-transparent border border-white text-white hover:bg-white hover:text-black hover:border-blue-300'
                            } ${categorizingComments.has(comment.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {categorizingComments.has(comment.id) ? '...' : 'QUESTION'}
                        </button>
                        <button
                          onClick={() => categorizeComment(comment.id, 'COMMENT')}
                          disabled={categorizingComments.has(comment.id)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-all duration-200 ${comment.category === 'COMMENT'
                              ? 'bg-green-600 text-white border-2 border-green-400 shadow-lg'
                              : 'bg-transparent border border-white text-white hover:bg-white hover:text-black hover:border-green-300'
                            } ${categorizingComments.has(comment.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {categorizingComments.has(comment.id) ? '...' : 'COMMENT'}
                        </button>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => toggleCommentVisibility(comment.id, !comment.isVisible)}
                          className={`px-3 py-1 rounded text-xs font-medium ${comment.isVisible
                              ? 'bg-green-600 text-white'
                              : 'bg-transparent border border-white text-white hover:bg-white hover:text-black'
                            }`}
                        >
                          {comment.isVisible ? 'VISIBLE' : 'HIDDEN'}
                        </button>
                        <button
                          onClick={() => dismissComment(comment.id)}
                          className="px-3 py-1 rounded text-xs font-medium bg-red-600 text-white hover:bg-red-700"
                        >
                          DISMISS
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatControl
